import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, MousePointer, Sparkles, Play } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  resourcesData, 
  useUserCloud, 
  EmailCaptureModal,
  PromptBlock, 
  EmergentCTA,
  AboutEmergent,
  RelatedResources, 
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = resourcesData['custom-cursor-effects'];

// Interactive cursor demo component
const CursorDemo = ({ type, label, description }) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [trail, setTrail] = useState([]);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setPosition({ x, y });

    if (type === 'glow' || type === 'sparkle') {
      setTrail(prev => [...prev.slice(-15), { x, y, id: Date.now() }]);
    }
  };

  useEffect(() => {
    if (trail.length > 0) {
      const timer = setTimeout(() => {
        setTrail(prev => prev.slice(1));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [trail]);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-gray-900">{label}</h4>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <span className="text-xs text-gray-400 flex items-center gap-1">
          <Play className="w-3 h-3" /> Hover to preview
        </span>
      </div>
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className="relative h-48 rounded-2xl overflow-hidden cursor-none"
        style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}
      >
        {/* Demo content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-white/30 text-sm">Move your cursor here</p>
        </div>

        {isHovering && (
          <>
            {/* Glow Trail */}
            {type === 'glow' && (
              <>
                {trail.map((point, i) => (
                  <motion.div
                    key={point.id}
                    initial={{ opacity: 0.8, scale: 1 }}
                    animate={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.5 }}
                    className="absolute w-3 h-3 rounded-full pointer-events-none"
                    style={{
                      left: point.x - 6,
                      top: point.y - 6,
                      background: 'radial-gradient(circle, #00CED1 0%, transparent 70%)',
                      boxShadow: '0 0 10px #00CED1',
                    }}
                  />
                ))}
                <div
                  className="absolute w-4 h-4 rounded-full pointer-events-none transition-transform duration-75"
                  style={{
                    left: position.x - 8,
                    top: position.y - 8,
                    background: '#00CED1',
                    boxShadow: '0 0 20px #00CED1, 0 0 40px #00CED1',
                  }}
                />
              </>
            )}

            {/* Binary Effect */}
            {type === 'binary' && (
              <>
                {trail.map((point, i) => (
                  <motion.div
                    key={point.id}
                    initial={{ opacity: 1, y: 0 }}
                    animate={{ opacity: 0, y: 30 }}
                    transition={{ duration: 1 }}
                    className="absolute pointer-events-none font-mono text-green-400 font-bold"
                    style={{
                      left: point.x - 6 + (Math.random() - 0.5) * 40,
                      top: point.y - 8,
                      fontSize: '14px',
                    }}
                  >
                    {Math.random() > 0.5 ? '1' : '0'}
                  </motion.div>
                ))}
                <div
                  className="absolute pointer-events-none font-mono text-green-400 font-bold"
                  style={{
                    left: position.x - 6,
                    top: position.y - 8,
                    fontSize: '16px',
                    textShadow: '0 0 10px #4ade80',
                  }}
                >
                  {Math.random() > 0.5 ? '1' : '0'}
                </div>
              </>
            )}

            {/* Sparkle Trail */}
            {type === 'sparkle' && (
              <>
                {trail.map((point, i) => (
                  <motion.div
                    key={point.id}
                    initial={{ opacity: 1, scale: 1, rotate: 0 }}
                    animate={{ opacity: 0, scale: 0, rotate: 180 }}
                    transition={{ duration: 0.6 }}
                    className="absolute pointer-events-none text-yellow-400"
                    style={{
                      left: point.x - 8 + (Math.random() - 0.5) * 20,
                      top: point.y - 8 + (Math.random() - 0.5) * 20,
                      fontSize: Math.random() * 8 + 8,
                    }}
                  >
                    ✦
                  </motion.div>
                ))}
                <div
                  className="absolute w-3 h-3 pointer-events-none"
                  style={{
                    left: position.x - 6,
                    top: position.y - 6,
                  }}
                >
                  <span className="text-yellow-400 text-lg">✦</span>
                </div>
              </>
            )}

            {/* Emoji */}
            {type === 'emoji' && (
              <motion.div
                className="absolute pointer-events-none text-2xl"
                animate={{ 
                  left: position.x - 14,
                  top: position.y - 14,
                  rotate: [0, 10, -10, 0],
                }}
                transition={{ 
                  left: { type: 'spring', damping: 25, stiffness: 200 },
                  top: { type: 'spring', damping: 25, stiffness: 200 },
                  rotate: { duration: 0.5, repeat: Infinity },
                }}
              >
                🔥
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const CustomCursorEffectsPage = () => {
  const navigate = useNavigate();
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // Check if user has access (either has email or this resource is saved)
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
    // TODO: Send email via backend/Beehiiv
  };

  // Show email capture if no access
  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <MousePointer className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              {resource.platform} Prompt
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Instructions */}
        <div className="mb-12 p-6 rounded-2xl bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-2">How to Use These Prompts</h3>
          <p className="text-gray-600 text-sm">
            Choose the cursor style that fits your brand personality. Copy the prompt, paste it into Emergent AI, and customize the details in brackets.
          </p>
        </div>

        {/* Interactive Demos */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Try the Effects</h3>
          <p className="text-gray-600 mb-6">Hover over each demo to preview the cursor effect in action.</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <CursorDemo 
              type="glow" 
              label="Glow Trail" 
              description="Magical, dreamy effect"
            />
            <CursorDemo 
              type="binary" 
              label="Binary Trail" 
              description="Matrix-style tech effect"
            />
            <CursorDemo 
              type="sparkle" 
              label="Sparkle Trail" 
              description="Glamorous, eye-catching"
            />
            <CursorDemo 
              type="emoji" 
              label="Emoji Follower" 
              description="Playful, memorable"
            />
          </div>
        </div>

        {/* Cursor Options Table */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Cursor Style Options</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Style</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Best For</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Vibe</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { style: 'Glow trail', bestFor: 'Creatives, artists', vibe: 'Magical, dreamy' },
                  { style: 'Dot with ring', bestFor: 'Minimalists, designers', vibe: 'Clean, modern' },
                  { style: 'Emoji follower', bestFor: 'Fun brands, Gen Z', vibe: 'Playful, memorable' },
                  { style: 'Color gradient blob', bestFor: 'Tech, innovation', vibe: 'Futuristic, dynamic' },
                  { style: 'Sparkle trail', bestFor: 'Lifestyle, fashion', vibe: 'Glamorous, eye-catching' },
                  { style: 'Custom shape', bestFor: 'Unique brands', vibe: 'On-brand, distinctive' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{row.style}</td>
                    <td className="py-3 px-4 text-gray-600">{row.bestFor}</td>
                    <td className="py-3 px-4 text-gray-600">{row.vibe}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Prompts */}
        <h3 className="text-2xl font-bold text-gray-900 mb-6">The Prompts</h3>

        <PromptBlock
          title="Custom Cursor Effect (Base Prompt)"
          description="Start here and customize based on your brand."
          prompt={`Add a custom cursor effect to my website.

Cursor style: [CHOOSE FROM OPTIONS ABOVE]

Specific requirements:
- Should work on desktop only (mobile uses default)
- Smooth animation, not laggy
- Subtle but noticeable
- Should not interfere with clicking links/buttons

[OPTIONAL CUSTOMIZATIONS:]
- Color: [YOUR BRAND COLOR]
- Size: [small / medium / large]
- Trail length: [short / medium / long]
- On hover over links: [grow larger / change color / pulse]`}
        />

        <PromptBlock
          title="Glow Trail Cursor"
          description="This is what I used on techbaenae.com. Creates a magical, dreamy effect."
          prompt={`Add a glowing cursor trail effect to my website:

- Small glowing dot follows the cursor
- Leaves a fading trail of smaller dots behind it
- Trail color: soft gradient (pick colors that match my site)
- Trail fades out over ~0.5 seconds
- Only shows on desktop
- Cursor changes to pointer on clickable elements
- Subtle glow effect, not overwhelming`}
        />

        <PromptBlock
          title="Interactive Blob Cursor"
          description="A more dynamic option that reacts to your movements."
          prompt={`Create an interactive blob cursor effect:

- Soft, organic blob shape follows cursor
- Blob morphs/distorts slightly as it moves
- On hover over interactive elements, blob expands
- Colors: [YOUR COLORS or "match my site's accent color"]
- Smooth, fluid animation
- Desktop only`}
        />

        <EmergentCTA />

        <PromptBlock
          title="Sparkle Trail"
          description="Perfect for lifestyle, beauty, or fashion brands."
          prompt={`Add a sparkle trail cursor effect:

- Small star/sparkle shapes appear behind cursor
- Sparkles fade out and fall slightly
- Random sizes for sparkles
- Colors: gold/silver OR match my brand colors
- Subtle and elegant, not overwhelming
- Desktop only`}
        />

        <PromptBlock
          title="Emoji Follower"
          description="Fun and memorable. Great for personality-driven brands."
          prompt={`Add a custom emoji cursor follower:

- Emoji: [YOUR EMOJI, e.g., ✨, 🚀, 💫]
- Emoji follows cursor with slight delay
- Subtle rotation as it moves
- On click: emoji grows briefly then shrinks back
- Desktop only
- Should not interfere with clicking`}
        />

        <AuthorCard author={resource.author} />
        
        <RelatedResources resourceIds={resource.relatedResources} />

        <AboutEmergent />
      </div>
    </div>
  );
};

export default CustomCursorEffectsPage;
